/**
 * AgentModelModal — the ONE shared "Agent & model" editor.
 *
 * Used by BOTH surfaces that change an agent/model + credential:
 *   - PIPELINES (PipelineDetailPage): full agent:model SSOT picker
 *     (`AgentModelSelect` seeded from `useAgentOptions()`), Claude gets a
 *     separate API-key + OAuth-token field. Caller's onSave PATCHes
 *     /workflows/{uuid} with {aiAgent, aiModel, aiApiKey, aiOAuthToken}.
 *   - APPS (AppDetailPage): vendor-only select (Claude / Codex), single
 *     credential field. Caller's onSave POSTs Apps.setAgent({provider, token}).
 *
 * The two used to be separate components that drifted visually. This is the
 * single source of truth for the chrome (mkt-modal), the field styling
 * (atomic Field + apps-modal-field-label), the validation gate, and the
 * masked-credential handling. The PARENT owns the request shape via `onSave`.
 *
 * Credentials arrive MASKED from the GET (sk-a...wxyz). We seed the field
 * with the mask and treat a value still containing '...' as "untouched" —
 * never sending it. A real change (or a vendor switch that empties the seed)
 * is validated by format before submit.
 *
 * Validation (the hard requirement — empty/invalid must NOT save):
 *   - A token is REQUIRED when there's no stored masked token to keep for the
 *     selected agent/provider. Empty → inline "API key required", no submit.
 *   - A TYPED token (not the mask) is format-checked:
 *       claude  → ^sk-ant-(oat01|api03)-   (api03 = API key, oat01 = OAuth)
 *       codex   → ^sk-
 *   - Save is disabled while invalid; submit shows the inline error.
 *   - Server errors map onto the same inline field slot (short, red).
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import Field from '../../atoms/Field/Field';
import {
  parseAgentOption, filterOptionsByAgents, firstOptionForAgent,
} from '../../../utils/agent-options';
import AgentModelFields, {
  validateToken, isMask, isClaudeOAuthToken, VENDOR_KEY_HINT, VENDOR_KEY_LABEL,
} from '../AgentModelFields/AgentModelFields';
import MultiAgentCredentialFields from '../AgentModelFields/MultiAgentCredentialFields';
import DeployInputsSection from '../../shared/SchemaForm/DeployInputsSection';
import {
  collectDeployInputFields,
  validateDeployInputs,
  buildDefaultInput,
} from '../../shared/SchemaForm/deployInputSources';
// Chrome classes (mkt-modal-overlay / mkt-modal / header / title / close /
// body / deploy / secondary) come from the marketplace detail modal stylesheet.
import '../../../pages/MarketplacePage/MarketplaceDetailModal.css';
import '../../atoms/Field/Field.css';
import './AgentModelModal.css';

export default function AgentModelModal({
  isOpen,
  onClose,
  title = 'Model & engine',
  variant,
  agentOptions = [],
  seedValue = '',
  seedKey = '',
  seedOAuth = '',
  onSave,
  onSaved,
  setToast,
  // Optional "also set as this project's default" opt-in. Shown only when the
  // caller asks (deploy-time, project has no key). When checked, the saved
  // body carries `saveToProject: true` so the caller can persist the key to
  // the project too — not just to the agent/app row.
  showSaveToProject = false,
  saveToProjectLabel = 'Save as project default',
  // CTA label. Deploy-from-marketplace passes "Save and deploy"; default Save.
  submitLabel = 'Save',
  // Optional: the template's inputSchema. When present (deploy flow), the
  // "Required inputs" section (repo picker etc.) renders ABOVE the key field.
  inputSchema = null,
  // Agent-code-driven picker (pipeline variant only). The distinct set of
  // agents the template's graph PINS (marketplace sync → entry.requiredAgents).
  //   []        → unconstrained: the full agent:model picker (unchanged).
  //   ['codex'] → single-agent LOCK: the model select is filtered to that
  //               vendor and the credential field labels for it.
  //   ['a','b'] → MULTI-agent: one credential slot per agent (its own filtered
  //               model select + key); onSave emits an `agentTokens` map.
  requiredAgents = [],
  // Multi-agent PREFILL: the project's already-saved masked key per vendor,
  // e.g. { claude: 'sk-a...wxyz', codex: 'sk-...abcd' }. Seeds each slot on
  // open so a project that already configured a vendor shows "✓ Saved" instead
  // of an empty field. A kept mask sends nothing → backend uses the project key.
  seedKeysByAgent = {},
}) {
  // `variant` decides the agent control: 'pipeline' = full agent:model
  // SSOT picker; 'app' = vendor-only (Claude / Codex) select.
  const isPipeline = variant === 'pipeline';

  // Agent-code-driven picker (pipeline only). See the `requiredAgents` prop doc.
  const reqAgents = useMemo(
    () => (Array.isArray(requiredAgents) ? requiredAgents.filter(Boolean) : []),
    [requiredAgents],
  );
  const isMultiAgent = isPipeline && reqAgents.length > 1;
  const isSingleAgent = isPipeline && reqAgents.length === 1;
  // Single-agent LOCK: constrain the picker to the one required vendor so the
  // select can't cross-switch agents. Unconstrained/multi use the full list
  // (multi filters per-slot inside MultiAgentCredentialFields).
  const effectiveOptions = useMemo(
    () => (isSingleAgent ? filterOptionsByAgents(agentOptions, reqAgents) : agentOptions),
    [isSingleAgent, agentOptions, reqAgents],
  );

  // Coerce seeds to strings — callers pass row fields that can be null OR a
  // non-string (which would render as "[object Object]" in the input).
  const str = (x) => (typeof x === 'string' ? x : '');
  const [value, setValue] = useState(str(seedValue));          // pipeline: 'claude:opus-4.8'
  const [provider, setProvider] = useState(str(seedValue) || 'claude'); // app: 'claude'|'codex'
  // ONE credential field. Seed it from whichever credential the row has — an
  // API key OR a Pro/Max OAuth token — so a stored OAuth-only cred still shows
  // as "✓ Saved" instead of an empty field.
  const [apiKey, setApiKey] = useState(str(seedKey) || str(seedOAuth));
  const [saving, setSaving] = useState(false);
  const [keyErr, setKeyErr] = useState('');   // inline error on the key field
  const [submitted, setSubmitted] = useState(false);
  // Credential fields start readOnly so the browser can't autofill a saved
  // password into them (decoys + autoComplete aren't enough in Chrome). They
  // unlock on first focus — by then autofill has already passed.
  const [credUnlocked, setCredUnlocked] = useState(false);
  const [saveToProject, setSaveToProject] = useState(true);

  // Multi-agent credential capture — one model + one key per required agent.
  // Keyed by agent name ('claude'|'codex'|'gemini'). Empty until seeded on open.
  const [multiModels, setMultiModels] = useState({});
  const [multiKeys, setMultiKeys] = useState({});

  // Deploy-time required inputs (repo picker etc.) — only active when the
  // caller passes an inputSchema that has deployInput annotations.
  const deployInputFields = useMemo(
    () => collectDeployInputFields(inputSchema),
    [inputSchema],
  );
  const [deployInputValues, setDeployInputValues] = useState({});
  const [deployInputErrors, setDeployInputErrors] = useState({});
  const updateDeployInput = useCallback((name, val) => {
    setDeployInputValues((prev) => ({ ...prev, [name]: val }));
    setDeployInputErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  // (Re)seed on open / when the underlying values change.
  useEffect(() => {
    if (!isOpen) return;
    setValue(str(seedValue));
    setProvider(str(seedValue) || 'claude');
    setApiKey(str(seedKey) || str(seedOAuth));
    setCredUnlocked(false);
    setKeyErr('');
    setSubmitted(false);
    setSaving(false);
    setDeployInputValues({});
    setDeployInputErrors({});
    // Reset multi-agent capture on every (re)open; models get defaulted by the
    // effect below once the option list is available. Keys are PRE-SEEDED from
    // the project's already-saved masked key per vendor so a configured agent
    // shows "✓ Saved" instead of an empty slot (kept mask → backend uses it).
    setMultiModels({});
    const seededMulti = {};
    for (const a of reqAgents) {
      const s = seedKeysByAgent?.[a];
      if (typeof s === 'string' && s) seededMulti[a] = s;
    }
    setMultiKeys(seededMulti);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, seedValue, seedKey, seedOAuth, reqAgents]);

  // Default each required agent's model to its newest option once the picker
  // list is available (useAgentOptions resolves async). Only fills slots the
  // user hasn't already set — never clobbers a manual pick or a typed key.
  useEffect(() => {
    if (!isOpen || !isMultiAgent) return;
    setMultiModels((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const a of reqAgents) {
        if (!next[a]) {
          const v = firstOptionForAgent(agentOptions, a);
          if (v) { next[a] = v; changed = true; }
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMultiAgent, agentOptions]);

  // Selected vendor — drives which credential fields show + how we validate.
  const vendor = useMemo(() => {
    if (isPipeline) return parseAgentOption(value).aiAgent;
    return provider;
  }, [isPipeline, value, provider]);

  const isClaude = vendor === 'claude';
  // EVERY agent here is BYOK and needs a key — claude (sk-ant-…),
  // codex (sk-…), gemini (AIza…). Only the "project default" empty
  // option (no vendor) needs none.
  const needsKey = Boolean(vendor);

  // The masked seed we'd keep if the user doesn't type. Switching vendor away
  // from the seeded one empties it (the stored token belongs to the other
  // vendor) — so a fresh key becomes required for the new vendor.
  const seededVendor = isPipeline
    ? parseAgentOption(str(seedValue)).aiAgent
    : (str(seedValue) || 'claude');
  const maskedSeed = vendor === seededVendor ? (str(seedKey) || str(seedOAuth)) : '';

  // When the user switches vendor, the stored key (for the old vendor) no
  // longer applies — clear it (or restore the mask if they switched back to
  // the seeded vendor) so the field reflects what we'd actually use.
  const prevVendorRef = useRef(vendor);
  useEffect(() => {
    if (prevVendorRef.current === vendor) return;
    prevVendorRef.current = vendor;
    setApiKey(vendor === seededVendor ? (str(seedKey) || str(seedOAuth)) : '');
    setKeyErr('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  // Live validity — gates the Save button (one credential field).
  const tokenError = needsKey
    ? validateToken({ vendor, value: apiKey, maskedSeed })
    : '';
  // Multi-agent: EVERY required agent needs a valid key. A slot pre-seeded from
  // the project (masked) counts as satisfied — keeping the mask is valid and
  // sends nothing (backend uses the stored project key). An empty slot with no
  // seed still requires a well-formed key.
  const multiValid = useMemo(() => {
    if (!isMultiAgent) return true;
    return reqAgents.every(
      (a) => !validateToken({
        vendor: a,
        value: multiKeys[a] || '',
        maskedSeed: seedKeysByAgent?.[a] || '',
        required: true,
      }),
    );
  }, [isMultiAgent, reqAgents, multiKeys, seedKeysByAgent]);
  const isValid = isMultiAgent ? multiValid : !tokenError;

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, saving, onClose]);

  if (!isOpen) return null;

  const tokenIsMask = isMask(apiKey);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    setSubmitted(true);
    // Validate required deploy inputs first (repo picker etc.).
    if (deployInputFields.length > 0) {
      const { ok, errors } = validateDeployInputs(deployInputFields, deployInputValues);
      if (!ok) {
        setDeployInputErrors(errors);
        if (!isValid) setKeyErr(tokenError);
        return;
      }
    }
    if (!isValid) {
      setKeyErr(tokenError);
      return;
    }
    setKeyErr('');

    // Build the surface-specific body. Only send a credential the user
    // actually changed (differs from the masked seed AND isn't the mask).
    let body;
    if (isMultiAgent) {
      // Multi-agent: the workflow's DEFAULT agent (for any unpinned node) is the
      // first required agent; pinned nodes run on their own agent regardless.
      const primary = reqAgents[0];
      const { aiModel: primaryModel } = parseAgentOption(multiModels[primary] || '');
      body = { aiAgent: primary, aiModel: primaryModel || null };
      // Structured per-agent key map — the deploy handler snapshots EACH onto
      // the workflow row's envSecret as that agent's canonical env var
      // (ANTHROPIC_API_KEY / OPENAI_API_KEY / GOOGLE_API_KEY), so every pinned
      // node has the credential it needs.
      const agentTokens = {};
      for (const a of reqAgents) {
        const v = (multiKeys[a] || '').trim();
        if (v && !isMask(v)) agentTokens[a] = v;
      }
      body.agentTokens = agentTokens;
      // Backward-compat: also surface the PRIMARY agent's key through the
      // existing single-credential fields so the legacy single-key snapshot
      // path credentials the workflow's default agent even where the
      // agentTokens map isn't consumed yet.
      const pv = agentTokens[primary];
      if (pv) {
        if (primary === 'claude') {
          if (isClaudeOAuthToken(pv)) body.aiOAuthToken = pv; else body.aiApiKey = pv;
        } else {
          body.aiApiKey = pv;
        }
      }
    } else if (isPipeline) {
      const { aiAgent, aiModel } = parseAgentOption(value);
      body = { aiAgent: aiAgent || null, aiModel: aiModel || null };
      // ONE field → route by prefix. A fresh entry replaces the active
      // credential and clears the other slot, so exactly one is in effect.
      if (needsKey && apiKey !== maskedSeed && !tokenIsMask) {
        const v = apiKey.trim();
        if (isClaude) {
          if (v === '') { body.aiApiKey = null; body.aiOAuthToken = null; }
          else if (isClaudeOAuthToken(v)) { body.aiOAuthToken = v; body.aiApiKey = null; }
          else { body.aiApiKey = v; body.aiOAuthToken = null; }
        } else {
          body.aiApiKey = v === '' ? null : v;
        }
      }
    } else {
      body = { provider };
      if (apiKey !== maskedSeed && !tokenIsMask && apiKey.trim() !== '') {
        body.token = apiKey.trim();
      }
    }
    if (showSaveToProject) body.saveToProject = saveToProject;
    const defaultInput = buildDefaultInput(deployInputFields, deployInputValues);
    if (defaultInput) body.defaultInput = defaultInput;

    setSaving(true);
    setKeyErr('');
    try {
      await onSave(body);
      setToast?.({ show: true, isOpen: true, message: 'Model updated', color: 'success' });
      onSaved?.();
      onClose?.();
    } catch (e2) {
      // Map the server error onto the field inline (short).
      setKeyErr(e2?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // The error shown on the key field: the live gate error after a submit
  // attempt, otherwise the server error (if any).
  const shownKeyErr = (submitted && tokenError) ? tokenError : keyErr;

  return createPortal(
    <div className="mkt-modal-overlay" role="presentation" onClick={() => !saving && onClose?.()}>
      <div
        className="mkt-modal agent-model-modal"
        style={{ maxWidth: 460 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="agent-model-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="mkt-modal-close"
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
        >
          <IonIcon icon={closeOutline} />
        </button>

        <div className="mkt-modal-body">
          <header className="mkt-modal-header">
            <h2 className="mkt-modal-title" id="agent-model-title">{title}</h2>
          </header>

          <form onSubmit={handleSave} autoComplete="off" className="agent-model-form">
            {/* Off-screen decoys soak up the browser's saved-login autofill so
                it can't dump a saved password into the token field. */}
            <input type="text" name="zibby-decoy-user" tabIndex={-1} autoComplete="username"
              aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />
            <input type="password" name="zibby-decoy-pass" tabIndex={-1} autoComplete="new-password"
              aria-hidden="true" style={{ position: 'absolute', opacity: 0, height: 0, width: 0, pointerEvents: 'none' }} />

            {/* Required deploy-time inputs (repo picker etc.) — only rendered
                when the template declares deployInput annotations on its
                inputSchema. Renders nothing for existing templates with no
                deploy inputs → backward compatible. */}
            <DeployInputsSection
              inputSchema={inputSchema}
              values={deployInputValues}
              onChange={updateDeployInput}
              errors={deployInputErrors}
              disabled={saving}
            />

            {isMultiAgent ? (
              // Multi-agent template: one credential slot per required agent.
              <MultiAgentCredentialFields
                requiredAgents={reqAgents}
                agentOptions={agentOptions}
                modelsByAgent={multiModels}
                onModelChange={(a, v) => setMultiModels((p) => ({ ...p, [a]: v }))}
                keysByAgent={multiKeys}
                onKeyChange={(a, v) => { setMultiKeys((p) => ({ ...p, [a]: v })); if (keyErr) setKeyErr(''); }}
                submitted={submitted}
                disabled={saving}
              />
            ) : isPipeline ? (
              // Pipeline / deploy / settings all render the ONE shared atomic
              // form so they look + behave identically. `effectiveOptions` is
              // the full list, or — for a single-agent template — filtered to
              // the one required vendor (locks the model select to it).
              <AgentModelFields
                agentOptions={effectiveOptions}
                value={value}
                onValueChange={(v) => { setValue(v); setKeyErr(''); }}
                apiKey={apiKey}
                onApiKeyChange={(v) => { setApiKey(v); if (keyErr) setKeyErr(''); }}
                submitted={submitted}
                serverKeyError={keyErr}
                disabled={saving}
                showSaveToProject={showSaveToProject}
                saveToProject={saveToProject}
                onSaveToProjectChange={setSaveToProject}
                saveToProjectLabel={saveToProjectLabel}
              />
            ) : (
              // App variant: vendor-only (Claude/Codex) select + single key.
              <>
                <div className="agent-model-field">
                  <label className="apps-modal-field-label" htmlFor="agent-model-pick">Model &amp; engine</label>
                  <select
                    id="agent-model-pick"
                    className="mkt-field mkt-field--select"
                    value={provider}
                    onChange={(e) => { setProvider(e.target.value); setKeyErr(''); }}
                    disabled={saving}
                    aria-label="Model provider for this app"
                  >
                    <option value="claude">Claude</option>
                    <option value="codex">Codex</option>
                  </select>
                </div>
                {needsKey && (
                  <div className="agent-model-field">
                    <label className="apps-modal-field-label" htmlFor="agent-model-key">
                      {VENDOR_KEY_LABEL[vendor] || 'API key'}
                    </label>
                    <Field
                      as="input"
                      id="agent-model-key"
                      type="text"
                      name="zibby-agent-token"
                      value={apiKey}
                      placeholder={VENDOR_KEY_HINT[vendor] || 'API key'}
                      onChange={(e) => { setApiKey(e.target.value); if (keyErr) setKeyErr(''); }}
                      readOnly={!credUnlocked}
                      onFocus={() => setCredUnlocked(true)}
                      autoComplete="new-password"
                      data-1p-ignore="true"
                      data-lpignore="true"
                      spellCheck={false}
                      disabled={saving}
                      status={shownKeyErr ? 'error' : null}
                      aria-invalid={shownKeyErr ? true : undefined}
                    />
                    {shownKeyErr && <p className="agent-model-field-error">{shownKeyErr}</p>}
                  </div>
                )}
              </>
            )}

            {isMultiAgent && (
              // Small helper, moved to sit right above the CTA (was at the top
              // of the multi-agent slot list). requiredAgents is template-driven.
              <p className="agent-model-multi-hint">
                This agent runs on {reqAgents.length} models — provide a key for each.
              </p>
            )}

            <div className="mkt-alert-modal-actions">
              <button
                type="button"
                onClick={handleSave}
                className="mkt-modal-deploy mkt-modal-deploy--full"
                disabled={saving || !isValid}
              >
                {saving ? (
                  /* Shared atomic loading pattern (globals.css .pill-button-loading*):
                     label keeps its box (visibility:hidden) so the width never
                     jumps, with the pure-CSS spinner overlaid dead-center —
                     same as PillButton. No "…" suffix. */
                  <span className="pill-button-loading">
                    <span className="pill-button-loading-label" aria-hidden="true">{submitLabel}</span>
                    <span className="pill-button-loading-spinner" aria-hidden="true" />
                  </span>
                ) : submitLabel}
              </button>
              <button type="button" className="mkt-alert-modal-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
}

AgentModelModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  /** 'pipeline' = agent:model SSOT picker; 'app' = vendor-only select. */
  variant: PropTypes.oneOf(['pipeline', 'app']).isRequired,
  /** Pipeline only: [{ value, label }] from useAgentOptions(). */
  agentOptions: PropTypes.array,
  /** Pipeline: 'claude:opus-4.8'. App: 'claude' | 'codex'. */
  seedValue: PropTypes.string,
  /** Masked stored key, e.g. 'sk-a...wxyz', or ''. */
  seedKey: PropTypes.string,
  /** Pipeline+Claude only: masked stored OAuth token, or ''. */
  seedOAuth: PropTypes.string,
  /** Caller does the request. Receives the surface-specific body. */
  onSave: PropTypes.func.isRequired,
  /** Called after a successful onSave (refresh). */
  onSaved: PropTypes.func,
  setToast: PropTypes.func,
  /** Show the "also set as project default" opt-in (adds saveToProject to body). */
  showSaveToProject: PropTypes.bool,
  saveToProjectLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  /** Distinct pinned agents (marketplace entry.requiredAgents). [] = full
   *  picker; [x] = single-agent lock; [x,y,…] = per-agent credential slots. */
  requiredAgents: PropTypes.arrayOf(PropTypes.string),
  /** Multi-agent prefill: project's masked key per vendor, e.g.
   *  { claude: 'sk-a...z', codex: 'sk-...d' }. Seeds each slot on open. */
  seedKeysByAgent: PropTypes.object,
};
